import React, { useState, useEffect } from "react";
import { ref, getDatabase, onValue } from "firebase/database";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, LineChart, Line, ResponsiveContainer,
} from "recharts";
import Loader from "../components/UI/loader";

const COLORS = ["#450696", "#7c3aed", "#a78bfa", "#c4b5fd"];
const STATUS_COLORS = {
  waiting:  "#eab308",
  seated:   "#3b82f6",
  done:     "#22c55e",
  canceled: "#ef4444",
};

const TOOLTIP_STYLE = {
  backgroundColor: "#fff",
  border: "1px solid #f3f4f6",
  borderRadius: "12px",
  padding: "8px 12px",
  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
  fontSize: "12px",
};

function StatCard({ label, value, color }) {
  return (
    <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-3.5 text-center">
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
      {color && <div className="w-4 h-1 rounded mx-auto mt-1.5" style={{ backgroundColor: color }} />}
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <div className="mx-5 mb-3 bg-white rounded-2xl shadow-card border border-gray-100 p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center py-6">
      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-2">
        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5"
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      </div>
      <p className="text-sm text-gray-400">No data yet</p>
    </div>
  );
}

const PERIODS = ["Day", "Week", "Month", "Year"];

function Analytics() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState("Day");
  const database = getDatabase();
  const email = localStorage.getItem("email");

  useEffect(() => {
    setLoading(true);
    const unsubscribe = onValue(ref(database, "customers"), (snapshot) => {
      setLoading(false);
      const data = snapshot.val();
      if (data) {
        setCustomers(Object.values(data).filter((c) => c.email === email));
      } else {
        setCustomers([]);
      }
    });
    return () => unsubscribe();
  }, []);

  const now = new Date();

  // ── Period filter ──────────────────────────────────────────────────────────
  function inPeriod(c) {
    const ts = new Date(c.timeStamp);
    if (period === "Day")   return ts.toDateString() === now.toDateString();
    if (period === "Week")  return ts >= new Date(now.getTime() - 7 * 86400000);
    if (period === "Month") return ts.getFullYear() === now.getFullYear() && ts.getMonth() === now.getMonth();
    if (period === "Year")  return ts.getFullYear() === now.getFullYear();
    return true;
  }

  const filtered = customers.filter(inPeriod);

  // ── Status breakdown ───────────────────────────────────────────────────────
  const statusBreakdown = ["waiting", "seated", "done", "canceled"].map((s) => ({
    name: s.charAt(0).toUpperCase() + s.slice(1),
    value: filtered.filter((c) => c.status === s).length,
    color: STATUS_COLORS[s],
  }));

  // ── Served = done ──────────────────────────────────────────────────────────
  const servedCount  = filtered.filter((c) => c.status === "done").length;
  const canceledCount = filtered.filter((c) => c.status === "canceled").length;
  const conversionRate = filtered.length > 0
    ? Math.round((servedCount / filtered.length) * 100)
    : 0;

  // ── Avg estimate time (done customers) ─────────────────────────────────────
  const doneCust = filtered.filter((c) => c.status === "done" && c.estimate);
  const avgWait = doneCust.length > 0
    ? Math.round(doneCust.reduce((s, c) => s + parseInt(c.estimate || 0), 0) / doneCust.length)
    : 0;

  // ── Avg group size ─────────────────────────────────────────────────────────
  const withCount = filtered.filter((c) => c.count);
  const avgGroup = withCount.length > 0
    ? (withCount.reduce((s, c) => s + (parseInt(c.count) || 1), 0) / withCount.length).toFixed(1)
    : "–";

  // ── Category distribution (all time) ──────────────────────────────────────
  const categoryDist = ["family", "friends", "couple", "single"].map((cat, i) => ({
    name: cat.charAt(0).toUpperCase() + cat.slice(1),
    value: customers.filter((c) => c.category === cat).length,
    color: COLORS[i],
  }));

  // ── Trend chart ────────────────────────────────────────────────────────────
  function buildTrend() {
    if (period === "Day") {
      // hours 0–23
      return Array.from({ length: 24 }, (_, i) => ({
        label: `${i}:00`,
        total: filtered.filter((c) => new Date(c.timeStamp).getHours() === i).length,
        done:  filtered.filter((c) => c.status === "done" && new Date(c.timeStamp).getHours() === i).length,
      })).filter((h) => h.total > 0);
    }
    if (period === "Week") {
      return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(now.getTime() - (6 - i) * 86400000);
        const ds = d.toDateString();
        return {
          label: `${d.getMonth() + 1}/${d.getDate()}`,
          total: customers.filter((c) => new Date(c.timeStamp).toDateString() === ds).length,
          done:  customers.filter((c) => c.status === "done" && new Date(c.timeStamp).toDateString() === ds).length,
        };
      });
    }
    if (period === "Month") {
      const days = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
      return Array.from({ length: days }, (_, i) => {
        const day = i + 1;
        return {
          label: String(day),
          total: filtered.filter((c) => new Date(c.timeStamp).getDate() === day).length,
          done:  filtered.filter((c) => c.status === "done" && new Date(c.timeStamp).getDate() === day).length,
        };
      }).filter((d) => d.total > 0);
    }
    if (period === "Year") {
      const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      return months.map((m, i) => ({
        label: m,
        total: customers.filter((c) => new Date(c.timeStamp).getFullYear() === now.getFullYear() && new Date(c.timeStamp).getMonth() === i).length,
        done:  customers.filter((c) => c.status === "done" && new Date(c.timeStamp).getFullYear() === now.getFullYear() && new Date(c.timeStamp).getMonth() === i).length,
      }));
    }
    return [];
  }
  const trend = buildTrend();

  // ── Peak hours (all time) ──────────────────────────────────────────────────
  const peakHours = Array.from({ length: 24 }, (_, i) => ({
    hour: `${i}:00`,
    count: customers.filter((c) => new Date(c.timeStamp).getHours() === i).length,
  })).filter((h) => h.count > 0);

  const periodLabel = period === "Day" ? "Today" : period === "Week" ? "Last 7 days" : period === "Month" ? "This month" : "This year";
  const trendTitle  = period === "Day" ? "Hourly Breakdown" : period === "Week" ? "Daily – Last 7 Days" : period === "Month" ? "Daily – This Month" : "Monthly – This Year";

  return (
    <div className="max-w-md mx-auto pb-4">
      <div className="px-5 pt-6 pb-3">
        <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
        <p className="text-sm text-gray-500 mt-0.5">Waitlist performance overview</p>
      </div>

      {/* Period selector */}
      <div className="flex gap-2 px-5 mb-4">
        {PERIODS.map((p) => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            className={`flex-1 py-2 rounded-xl text-xs font-semibold transition-colors ${
              period === p
                ? "bg-primary text-white"
                : "bg-white border border-gray-200 text-gray-500"
            }`}
          >
            {p}
          </button>
        ))}
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 gap-2.5 mx-5 mb-3">
        <StatCard label={`Total (${periodLabel})`} value={filtered.length} />
        <StatCard label="Served" value={servedCount} color="#22c55e" />
        <StatCard label="Canceled" value={canceledCount} color="#ef4444" />
        <StatCard label="Conversion" value={`${conversionRate}%`} color="#450696" />
      </div>

      {/* Avg metrics */}
      <div className="mx-5 mb-3 grid grid-cols-2 gap-2.5">
        <div className="bg-primary rounded-2xl p-4 text-center">
          <p className="text-purple-200 text-xs uppercase tracking-wide">Avg Wait Est.</p>
          <p className="text-3xl font-bold text-white mt-1">{avgWait}m</p>
        </div>
        <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-4 text-center">
          <p className="text-gray-400 text-xs uppercase tracking-wide">Avg Group</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{avgGroup}</p>
        </div>
      </div>

      {/* Status breakdown */}
      <SectionCard title={`Status Breakdown (${periodLabel})`}>
        <div className="flex justify-around">
          {statusBreakdown.map((s) => (
            <div key={s.name} className="text-center">
              <div
                className="w-11 h-11 rounded-xl mx-auto flex items-center justify-center text-white font-bold text-sm"
                style={{ backgroundColor: s.color }}
              >
                {s.value}
              </div>
              <p className="text-xs mt-1.5 text-gray-600 font-medium">{s.name}</p>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Trend chart */}
      <SectionCard title={trendTitle}>
        {trend.length > 0 ? (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="total" fill="#a78bfa" radius={[4, 4, 0, 0]} name="Total" />
              <Bar dataKey="done"  fill="#450696" radius={[4, 4, 0, 0]} name="Served" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState />
        )}
      </SectionCard>

      {/* Category distribution (all time) */}
      <SectionCard title="Category Distribution (All Time)">
        {categoryDist.some((c) => c.value > 0) ? (
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={categoryDist.filter((c) => c.value > 0)}
                cx="50%" cy="50%"
                innerRadius={45} outerRadius={80}
                dataKey="value"
                label={({ name, value }) => `${name}: ${value}`}
                strokeWidth={0}
              >
                {categoryDist.filter((c) => c.value > 0).map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip contentStyle={TOOLTIP_STYLE} />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState />
        )}
      </SectionCard>

      {/* Peak hours (all time) */}
      <SectionCard title="Peak Hours (All Time)">
        {peakHours.length > 0 ? (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={peakHours}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="hour" tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="count" fill="#450696" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState />
        )}
      </SectionCard>

      {loading && <Loader loading={loading} />}
    </div>
  );
}

export default Analytics;
